import {
  AccessRole,
  AuditAction,
  CatalogKind,
  DisplayStatus,
  EquipmentOwnership,
  EquipmentStatus,
  ProjectStatus,
  Section,
  SubcontractorStatus,
  Workflow,
} from 'api/types'
import { activeLocale, applyDigits, formatDateValue, getRegion } from 'lib/region'

const localized = <T extends string>(ar: Record<T, string>, en: Record<T, string>): Record<T, string> =>
  new Proxy(ar, {
    get(target, prop, receiver) {
      if (typeof prop === 'string' && Object.prototype.hasOwnProperty.call(target, prop)) {
        return activeLocale() === 'en' ? en[prop as T] : target[prop as T]
      }
      return Reflect.get(target, prop, receiver)
    },
  })

const localizedList = (ar: readonly string[], en: readonly string[]): string[] =>
  new Proxy(ar as string[], {
    get(target, prop, receiver) {
      if (typeof prop === 'string' && /^\d+$/.test(prop)) {
        const list = activeLocale() === 'en' ? en : target
        return list[Number(prop)]
      }
      return Reflect.get(target, prop, receiver)
    },
  })

export const STATUS_LABELS: Record<DisplayStatus, string> = localized(
  { COMPLETE: 'مكتمل', CREATED: 'مُنشأ', DRAFT: 'مسودة', MISSING: 'مفقود', NEW: 'جديد' },
  { COMPLETE: 'Complete', CREATED: 'Created', DRAFT: 'Draft', MISSING: 'Missing', NEW: 'New' }
)

export const STATUS_LABELS_EN: Record<DisplayStatus, string> = {
  COMPLETE: 'Complete',
  CREATED: 'Created',
  DRAFT: 'Draft',
  MISSING: 'Missing',
  NEW: 'New',
}

export const STATUS_ORDER: DisplayStatus[] = ['COMPLETE', 'CREATED', 'DRAFT', 'MISSING', 'NEW']

export const WORKFLOW_LABELS: Record<Workflow, string> = localized(
  {
    APPROVED: 'معتمد',
    DRAFT: 'مسودة',
    EMPTY: 'لم يبدأ',
    RETURNED: 'تقرير للمراجعة',
    SUBMITTED: 'بانتظار الاعتماد',
  },
  {
    APPROVED: 'Approved',
    DRAFT: 'Draft',
    EMPTY: 'Not started',
    RETURNED: 'Report for review',
    SUBMITTED: 'Waiting for approval',
  }
)

export const SECTION_LABELS: Record<Section, string> = localized(
  {
    EQUIPMENT: 'المعدات',
    LAB_TESTS: 'الفحوصات المخبرية',
    MATERIALS: 'المواد الموردة',
    STAFF: 'الكوادر',
    SUPERVISION: 'ملاحظات الإشراف',
    VISITS: 'الزيارات',
    WORKS: 'وصف الأعمال',
  },
  {
    EQUIPMENT: 'Equipment',
    LAB_TESTS: 'Lab tests',
    MATERIALS: 'Delivered materials',
    STAFF: 'Crew',
    SUPERVISION: 'Supervision notes',
    VISITS: 'Visits',
    WORKS: 'Work description',
  }
)

export const SECTION_ORDER: Section[] = ['STAFF', 'LAB_TESTS', 'MATERIALS', 'EQUIPMENT', 'WORKS', 'VISITS', 'SUPERVISION']

const ROLE_AR: Record<AccessRole, string> = {
  ADMIN: 'مدير النظام',
  PROJECT_MANAGER: 'مدير المشروع',
  SITE_ENGINEER: 'مهندس الموقع',
  SUPERVISOR: 'الإشراف',
}

export const ROLE_LABELS_EN: Record<AccessRole, string> = {
  ADMIN: 'Admin',
  PROJECT_MANAGER: 'Project Manager',
  SITE_ENGINEER: 'Site Engineer',
  SUPERVISOR: 'Supervisor',
}

export const ROLE_LABELS: Record<AccessRole, string> = localized(ROLE_AR, ROLE_LABELS_EN)

export const roleLabel = (role: AccessRole, locale: 'ar' | 'en') => (locale === 'en' ? ROLE_LABELS_EN[role] : ROLE_AR[role])

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = localized(
  { ACTIVE: 'نشط', COMPLETED: 'مكتمل', ON_HOLD: 'متوقف' },
  { ACTIVE: 'Active', COMPLETED: 'Completed', ON_HOLD: 'On hold' }
)

export const EQUIPMENT_STATUS_LABELS: Record<EquipmentStatus, string> = localized(
  { BREAKDOWN: 'عطل', IDLE: 'خاملة', WORKING: 'تعمل' },
  { BREAKDOWN: 'Breakdown', IDLE: 'Idle', WORKING: 'Working' }
)

export const EQUIPMENT_STATUS_BADGE_CLASS: Record<EquipmentStatus, string> = {
  BREAKDOWN: 'badge badge--missing',
  IDLE: 'badge badge--new',
  WORKING: 'badge badge--complete',
}

export const EQUIPMENT_OWNERSHIP_LABELS: Record<EquipmentOwnership, string> = localized(
  { OWNED: 'مملوكة', RENTED: 'مستأجرة' },
  { OWNED: 'Owned', RENTED: 'Rented' }
)

export const SUBCONTRACTOR_STATUS_LABELS: Record<SubcontractorStatus, string> = localized(
  { ACTIVE: 'نشط', ENDED: 'منتهٍ', ON_HOLD: 'متوقف' },
  { ACTIVE: 'Active', ENDED: 'Ended', ON_HOLD: 'On hold' }
)

export const SUBCONTRACTOR_STATUS_BADGE_CLASS: Record<SubcontractorStatus, string> = {
  ACTIVE: 'badge badge--complete',
  ENDED: 'badge badge--missing',
  ON_HOLD: 'badge badge--draft',
}

export const CATALOG_LABELS: Record<CatalogKind, string> = localized(
  { EQUIPMENT: 'المعدات', JOB_TITLE: 'المسميات الوظيفية', MATERIAL: 'المواد', TEST_TYPE: 'أنواع الفحوصات' },
  { EQUIPMENT: 'Equipment', JOB_TITLE: 'Job titles', MATERIAL: 'Materials', TEST_TYPE: 'Test types' }
)

export const AUDIT_LABELS: Record<AuditAction, string> = localized(
  {
    APPROVE: 'اعتماد التقرير',
    ATTACH: 'إرفاق ملف',
    DETACH: 'حذف مرفق',
    NOTE: 'إضافة ملاحظة إشراف',
    NOTE_DELETE: 'حذف ملاحظة إشراف',
    REOPEN: 'إعادة فتح التقرير',
    RETURN: 'إرسال للمراجعة',
    SUBMIT: 'إرسال للاعتماد',
    UPDATE: 'تعديل المحتوى',
  },
  {
    APPROVE: 'Approve report',
    ATTACH: 'Attach file',
    DETACH: 'Remove attachment',
    NOTE: 'Add supervision note',
    NOTE_DELETE: 'Delete supervision note',
    REOPEN: 'Reopen report',
    RETURN: 'Send for review',
    SUBMIT: 'Submit for approval',
    UPDATE: 'Edit content',
  }
)

export const ARABIC_MONTHS = localizedList(
  ['كانون الثاني', 'شباط', 'آذار', 'نيسان', 'أيار', 'حزيران', 'تموز', 'آب', 'أيلول', 'تشرين الأول', 'تشرين الثاني', 'كانون الأول'],
  ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
)

export const WEEK_DAYS = localizedList(
  ['السبت', 'الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة'],
  ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
)
export const WEEK_DAYS_EN = ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']

const WEATHER_AR = ['مشمس', 'غائم جزئياً', 'غائم', 'ماطر', 'عاصف', 'مغبر', 'حار', 'بارد']
const WEATHER_EN = ['Sunny', 'Partly cloudy', 'Cloudy', 'Rainy', 'Windy', 'Dusty', 'Hot', 'Cold']

/** Stored on the report in Arabic. The list below is the saved value, never the translated label. */
export const WEATHER_OPTIONS = WEATHER_AR

export const weatherLabel = (value: string) => {
  const index = WEATHER_AR.indexOf(value)
  if (index < 0) return value
  return activeLocale() === 'en' ? WEATHER_EN[index] : value
}

export const weatherValue = (label: string) => {
  const index = WEATHER_EN.indexOf(label)
  return index >= 0 ? WEATHER_AR[index] : label
}

const pad = (value: number) => String(value).padStart(2, '0')

export const parseIso = (iso: string) => {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day))
}

export const toIso = (date: Date) =>
  `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`

export const monthKey = (iso: string) => iso.slice(0, 7)

export const monthBounds = (key: string) => {
  const [year, month] = key.split('-').map(Number)
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate()
  return { from: `${key}-01`, to: `${key}-${pad(last)}` }
}

export const monthTitle = (key: string) => {
  const [year, month] = key.split('-').map(Number)
  return `${ARABIC_MONTHS[month - 1]} ${year}`
}

export const monthsBetween = (start: string, end: string) => {
  const months: string[] = []
  let [year, month] = start.split('-').map(Number)
  const [endYear, endMonth] = end.split('-').map(Number)
  while (year < endYear || (year === endYear && month <= endMonth)) {
    months.push(`${year}-${pad(month)}`)
    month += 1
    if (month > 12) {
      month = 1
      year += 1
    }
  }
  return months
}

export const addDays = (iso: string, days: number) => {
  const date = parseIso(iso)
  date.setUTCDate(date.getUTCDate() + days)
  return toIso(date)
}

export const daysBetween = (startIso: string, endIso: string) =>
  Math.round((parseIso(endIso).getTime() - parseIso(startIso).getTime()) / 86400000)

/** Week starts on Saturday: returns the Saturday on or before the date. */
export const weekStart = (iso: string) => addDays(iso, -((parseIso(iso).getUTCDay() + 1) % 7))

export const formatDate = (iso?: string | null) => {
  if (!iso) return ''
  const [year, month, day] = iso.slice(0, 10).split('-')
  return applyDigits(formatDateValue(year, month, day))
}

export const formatDateTime = (value?: string | null) => {
  if (!value) return ''
  const region = getRegion()
  const hour12 = region.timeFormat === '12'
  const parts = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    hour: '2-digit',
    hourCycle: hour12 ? 'h12' : 'h23',
    minute: '2-digit',
    month: '2-digit',
    timeZone: 'Asia/Amman',
    year: 'numeric',
  }).formatToParts(new Date(value))
  const pick = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? ''
  const date = formatDateValue(pick('year'), pick('month'), pick('day'))
  const clock = `${pick('hour')}:${pick('minute')}`
  const period = hour12 ? pick('dayPeriod') : ''
  return applyDigits(`${date} ${period ? `${clock} ${period}` : clock}`)
}

export const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export const formatNumber = (value: number) =>
  applyDigits(new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value))

export const isImage = (mimeType: string) => mimeType.startsWith('image/')

export const codeWithoutMonth = (code: string) => code.replace(/-\d{2}-\d{4}$/, '')
